// Fathom analytics. The tracker script is injected into production builds only (the
// `fathom-analytics` plugin in vite.config.mts), and an ad blocker can remove it there too, so
// every call here is a no-op while `window.fathom` is missing.
declare global {
    interface Window {
        fathom?: { trackEvent: (name: string, options?: { _value?: number }) => void };
    }
}

export const trackEvent = (name: string): void => {
    if (!name) return;
    window.fathom?.trackEvent(name);
};

// Fixed event names, for actions whose buttons carry different text (or none) from place to place.
export const NEW_CHARACTER_EVENT = 'New Character';
export const CHARACTER_CREATED_EVENT = 'New Character Saved';
export const QUICK_CREATE_GUIDED_EVENT = 'Quick Create: Guided Setup';
export const QUICK_CREATE_TRADITIONAL_EVENT = 'Quick Create: Traditional Builder';
// Campaign logs what the click did (App.vue's onQuickCreateCampaignClick): a campaign started by a
// user with a plan, or the upgrade prompt shown to a free user.
export const QUICK_CREATE_CAMPAIGN_EVENT = 'Quick Create: Campaign';
export const QUICK_CREATE_CAMPAIGN_UPGRADE_EVENT = 'Quick Create: Campaign (Upgrade Prompt)';

// Clicks logged by one document-wide listener, each with how its event is named. The first
// matching selector wins, so the quick-create entries come before the drawer they sit in:
// - the quick-create menu's character entries (App.vue), each under a fixed name of its own — they
//   share the <label> "Character". Its Campaign entry is matched only to be skipped: its handler
//   logs it, by outcome;
// - the navigation drawer's entries (App.vue) and the Guided Setup start-flow buttons
//   (GuidedSetup.vue), under their own <label> text;
// - the new-character buttons (CharacterCreatorView.vue, CharacterList.vue), under one fixed name.
const CLICK_EVENTS: { selector: string; name: (target: Element) => string | undefined }[] = [
    { selector: '#quick-create-menu-button .quick-create-guided', name: () => QUICK_CREATE_GUIDED_EVENT },
    { selector: '#quick-create-menu-button .quick-create-traditional', name: () => QUICK_CREATE_TRADITIONAL_EVENT },
    { selector: '#quick-create-menu-button .quick-create-campaign', name: () => undefined },
    {
        selector: '#navigation-drawer > .nav-items .btn, .guided-setup .start-flow',
        name: (target) => target.querySelector('label')?.textContent?.trim(),
    },
    { selector: '.character-create', name: () => NEW_CHARACTER_EVENT },
];

const trackClick = (event: Event): void => {
    if (!(event.target instanceof Element)) return;

    for (const { selector, name } of CLICK_EVENTS) {
        const target = event.target.closest(selector);
        if (target) {
            trackEvent(name(target) ?? '');
            return;
        }
    }
};

// Listens in the capture phase: the drawer's <summary> stops propagation of its clicks, so a
// bubbling listener would never see them. Returns the matching uninstall.
export const installClickTracking = (root: Document | HTMLElement = document): (() => void) => {
    root.addEventListener('click', trackClick, true);
    return () => root.removeEventListener('click', trackClick, true);
};
